import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from './UserContext';
import CreditModal from './CreditModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faUser, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../api/apiClient';

const Header = ({ openLoginModal }) => {
  const { currentUser, logout, notifications, clearNotifications } = useContext(UserContext);
  const navigate = useNavigate();

  const [credits, setCredits] = useState(0);
  const [isCreditModalOpen, setCreditModalOpen] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);

  // Hämta användarens credits
  const fetchCredits = async (userId) => {
    try {
      const response = await api.get(`/api/get-credits?user_id=${userId}`);
      return response.data.credits;
    } catch (error) {
      console.error("Fel vid hämtning av credits:", error);
      return 0;
    }
  };

  useEffect(() => {
    if (!currentUser?.id) return;
    (async () => {
      const c = await fetchCredits(currentUser.id);
      setCredits(c || 0);
    })();
  }, [currentUser]);

  const fetchAndSetCredits = async () => {
    if (!currentUser?.id) return;
    const updated = await fetchCredits(currentUser.id);
    setCredits(updated || 0);
  };

  const handleOpenCreditModal = () => setCreditModalOpen(true);
  const handleCloseCreditModal = () => setCreditModalOpen(false);
  const toggleMenu = () => setMenuOpen(!isMenuOpen);

  // Hantera klick på "Skapa Bok"
  const handleCreateClick = (e) => {
    e.preventDefault();
    if (!currentUser) {
      toast.info('Du måste vara inloggad för att skapa en bok', {
        position: 'top-center',
        autoClose: 3000,
      });
    } else {
      navigate('/skapa');
    }
  };

  return (
    <>
      <header className="header bg-[#282c34] text-white w-full fixed top-0 z-50 shadow-md">
        <div className="container flex items-center justify-between px-4 py-3 max-w-none">
          {/* Logotyp */}
        <div className="flex items-center space-x-3 sm:space-x-6">
  <Link to="/">
    <img 
      src="/images/logo.webp" 
      alt="Barnboken" 
      className="w-14 h-14 sm:w-20 sm:h-20"
    />
  </Link>
  <h1 className="text-warmWhite font-bold text-xl sm:text-3xl neon-text">
    <Link to="/">Barnboken.se</Link>
  </h1>
</div>





          {/* Navigation större skärm */}
          <nav className="hidden lg:flex items-center">
            <ul className="flex items-center space-x-6 text-warmWhite font-semibold">
              <li><Link to="/">Hem</Link></li>
              <li><Link to="/produkter">Böcker</Link></li>
              <li><Link to="/publika">Publika</Link></li>
              <li><Link to="/affiliate">Affiliate</Link></li>
              <li>
                <button
                  onClick={handleCreateClick}
                  className="cursor-pointer hover:text-gold transition-colors duration-300"
                >
                  Skapa Bok
                </button>
              </li>

              {currentUser ? (
                <>
                  <li>
                    <button
                      onClick={handleOpenCreditModal}
                      className="flex items-center space-x-2 bg-turquoise px-3 py-1 rounded hover:scale-110 transform transition-transform duration-200"
                    >
                      <FontAwesomeIcon icon={faStar} className="text-softYellow" />
                      <span className="text-warmWhite font-semibold">{credits}</span>
                    </button>
                  </li>
                  <li>
                    <Link to="/dashboard" className="relative flex items-center">
                      <FontAwesomeIcon icon={faUser} className="mr-2" />
                      {currentUser.username || 'Konto'}
                      {notifications.length > 0 && (
                        <span
                          className="absolute top-0 right-0 w-5 h-5 flex items-center justify-center text-xs bg-red-500 text-white rounded-full"
                          onClick={(e) => {
                            e.preventDefault();
                            clearNotifications();
                          }}
                          title="Rensa notifikationer"
                        >
                          {notifications.length}
                        </span>
                      )}
                    </Link>
                  </li>
                  <li>
                    <span
                      onClick={logout}
                      className="cursor-pointer hover:text-gold transition-colors duration-300"
                    >
                      Logga ut
                    </span>
                  </li>
                </>
              ) : (
                <li>
                  <span
                    onClick={openLoginModal}
                    className="cursor-pointer hover:text-gold transition-colors duration-300"
                  >
                    Logga In
                  </span>
                </li>
              )}
            </ul>
          </nav>

          {/* Hamburgermeny-knapp */}
          <button
            className="lg:hidden text-white focus:outline-none"
            onClick={toggleMenu}
          >
            <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} className="w-6 h-6" />
          </button>
        </div>

        {/* Mobilmeny */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-16 left-0 w-full bg-[#282c34] shadow-md">
            <ul className="flex flex-col text-center space-y-4 p-4">
              <li>
                <Link to="/" className="block text-white" onClick={toggleMenu}>
                  Hem
                </Link>
              </li>
              <li>
                <Link to="/produkter" className="block text-white" onClick={toggleMenu}>
                  Produkter
                </Link>
              </li>
              <li>
                <Link to="/publika" className="block text-white" onClick={toggleMenu}>
                  Publika
                </Link>
              </li>
              <li>
                <Link to="/affiliate" className="block text-white" onClick={toggleMenu}>
                  Affiliate
                </Link>
              </li>
              <li>
                <button
                  onClick={() => {
                    handleCreateClick();
                    toggleMenu();
                  }}
                  className="block text-white w-full text-left"
                >
                  Skapa Bok
                </button>
              </li>

              {currentUser ? (
                <>
                  <li>
                    <button
                      onClick={() => {
                        handleOpenCreditModal();
                        toggleMenu();
                      }}
                      className="w-full flex justify-center items-center space-x-2 bg-turquoise px-3 py-2 rounded"
                    >
                      <FontAwesomeIcon icon={faStar} className="text-softYellow" />
                      <span className="font-semibold">{credits}</span>
                    </button>
                  </li>
                  <li>
                    <Link
                      to="/dashboard"
                      className="block text-white"
                      onClick={toggleMenu}
                    >
                      <FontAwesomeIcon icon={faUser} className="mr-2" />
                      {currentUser.username || 'Konto'}
                    </Link>
                  </li>
                  <li>
                    <button
                      onClick={() => {
                        logout();
                        toggleMenu();
                      }}
                      className="block text-white w-full text-left"
                    >
                      Logga ut
                    </button>
                  </li>
                </>
              ) : (
                <li>
                  <button
                    onClick={() => {
                      openLoginModal();
                      toggleMenu();
                    }}
                    className="block text-white w-full text-left"
                  >
                    Logga In
                  </button>
                </li>
              )}
            </ul>
          </div>
        )}
      </header>

      <CreditModal
        isOpen={isCreditModalOpen}
        onClose={handleCloseCreditModal}
        onPaymentSuccess={fetchAndSetCredits}
      />

      {/* Toast-container */}
      <ToastContainer />
    </>
  );
};

export default Header;
